import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useGame } from "../../store/game";
import { useHaptic } from "../../hooks/useHaptic";
import styles from "../BurgerMenu.module.css"; // Reuse sheet styles

type Task = {
  id: number;
  type: string;
  title: string;
  reward: number;
  target?: string;
  completed: boolean;
};

export function TasksModal() {
  const activeModal = useGame((s) => s.activeModal);
  const closeModal = useGame((s) => s.closeModal);
  const initSession = useGame((s) => s.initSession);
  const haptic = useHaptic();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const open = activeModal === "tasks";
  const close = () => { if (activeModal === "tasks") closeModal(); };

  useEffect(() => {
    if (open) {
      fetchTasks();
    }
  }, [open]);

  const fetchTasks = async () => {
    try {
      const tgInitData = window.Telegram?.WebApp?.initData || "mock";
      const apiUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${apiUrl}/api/tasks`, {
        headers: { Authorization: `tma ${tgInitData}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const completeTask = async (task: Task) => {
    if (task.completed || loadingId !== null) return;
    
    // For social tasks, ideally open the link first
    if (task.type === "CHANNEL_SUB" && task.target) {
      window.Telegram?.WebApp?.openTelegramLink?.(`https://t.me/${task.target.replace("@", "")}`);
    }

    haptic.impact("medium");
    setLoadingId(task.id);
    
    // Give them a moment to "subscribe"
    await new Promise(r => setTimeout(r, 2000));

    try {
      const tgInitData = window.Telegram?.WebApp?.initData || "mock";
      const apiUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${apiUrl}/api/tasks/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `tma ${tgInitData}`
        },
        body: JSON.stringify({ taskId: task.id })
      });
      
      const setGameTokens = useGame.getState().initSession; // Refresh balance
      
      if (res.ok) {
        haptic.notify("success");
        await fetchTasks();
        await setGameTokens();
      } else {
        haptic.notify("error");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={styles.backdrop}
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          />
          <motion.div
            className={styles.sheet}
            style={{ height: "80vh" }} // taller for tasks
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 36 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) close();
            }}
          >
            <span className={styles.handle} />
            <h2 className={styles.title}>Earn GT</h2>
            
            <p style={{ textAlign: "center", color: "var(--fg-muted)", marginBottom: "1rem" }}>
              Complete tasks to earn GT for the Airdrop!
            </p>

            <div className={styles.list}>
              {tasks.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  className={styles.item}
                  onClick={() => completeTask(task)}
                  disabled={task.completed || loadingId === task.id}
                  style={{ opacity: task.completed ? 0.6 : 1 }}
                >
                  <span className={styles.icon}>{task.type === "CHANNEL_SUB" ? "📢" : "🫂"}</span>
                  <span className={styles.body}>
                    <span className={styles.label}>{task.title}</span>
                    <span className={styles.subtitle}>+{task.reward} GT</span>
                  </span>
                  
                  <span className={styles.gtCounter} style={{ color: task.completed ? "#4ade80" : "" }}>
                     {loadingId === task.id ? "..." : (task.completed ? "✓" : "Claim")}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
